import type { OrderListItem, OrderListLine } from '@agos/api-client/model'
import { FiskalStornoAction, T, formatMoney, lineTotalCents, formatCents, minutesSince, tt } from '@agos/shared'
import { BUTTON_GHOST, cn } from '@agos/ui'
import { Ban, BellRing, Check, CheckCheck, Clock, Pencil, Wallet, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { allServed, effectiveServed, useServedLines } from '../restaurant/served-lines'
import { statusForOrder, type TableStatus } from './table-status'

import { noteOf } from '../restaurant/rounds'

/**
 * The panel that slides in when a waiter taps an occupied table.
 *
 * Its real job is the served checklist. A waiter carrying four plates from the
 * pass ticks them off here, and the tile turns green when the last one lands —
 * which is why per-item progress outranks the order's kitchen status on the
 * floor plan.
 *
 * The ticks are optimistic: the tap has to register before the network does, or
 * a waiter with their hands full taps twice.
 */

const STATUS_BANNERS: Partial<Record<TableStatus, { icon: typeof Clock; text: string; className: string }>> =
  {
    ready: {
      icon: BellRing,
      text: tt('Kuchyňa označila objednávku ako hotovú - pripravte na servírovanie.'),
      className:
        'bg-blue-500/15 border-blue-500/30 text-blue-700 dark:text-blue-300',
    },
    in_kitchen: {
      icon: Clock,
      text: tt('Časť objednávky je ešte v kuchyni.'),
      className: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300',
    },
  }

export interface OrderSidePanelProps {
  title: string
  subtitle?: string
  order: OrderListItem | null
  /** `null` for a pickup order, which belongs to no table. */
  tableName: string | null
  now: number
  onClose: () => void
  onItemsChanged: () => void
  onGoToPay: () => void
  onGoToAdd: () => void
  onGoToCancel: () => void
  /**
   * §9.744, owner O114 - `edit_order` and `new_order`, the rights *Upraviť* asks (`mayOpenOrderEditor`): it opens the
   * order in the editor, whose route asks the second and whose save the server refuses without the first (`BACKEND.md`
   * §10A.397). Required, so the floor plan cannot draw the panel without it.
   */
  canEdit: boolean
}

export function OrderSidePanel({
  title,
  subtitle,
  order,
  tableName,
  now,
  onClose,
  onItemsChanged,
  onGoToPay,
  onGoToAdd,
  onGoToCancel,
  canEdit,
}: OrderSidePanelProps) {
  // §9.127 — the same writer *Otvorené stoly* uses. This panel's own copy held only the wanted
  // value and never cleared it, so another till un-serving a line left this screen showing it served
  // for the rest of the session.
  const { ticks, isMarkingAll, setServed, markAllServed } = useServedLines(onItemsChanged)

  if (!order) {
    return (
      <PanelShell title={title} subtitle={subtitle} status="free" onClose={onClose}>
        <div className="flex-1 flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
          <T>Na tomto stole nie je aktívna objednávka.</T>
        </div>
      </PanelShell>
    )
  }

  const lines = order.items ?? []
  const status = statusForOrder(order)
  const servedOf = (line: OrderListLine): string => effectiveServed(line, ticks)
  const everythingServed = allServed(lines, ticks)

  const minutesAgo = order.createdAt ? minutesSince(order.createdAt, now) : null
  const banner = STATUS_BANNERS[status]

  return (
    <PanelShell
      title={title}
      subtitle={[
        subtitle,
        `#${order.orderNumber}`,
        minutesAgo === null ? null : minutesAgo === 0 ? tt('práve teraz') : `${minutesAgo} min`,
        order.employeeName ? `${tt('Obsluha')}: ${order.employeeName}` : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      status={status}
      onClose={onClose}
    >
      {banner && (
        <div
          className={cn(
            'px-4 py-2 border-b flex items-center gap-2 text-xs font-bold',
            banner.className,
          )}
        >
          <banner.icon className={cn('w-4 h-4', status === 'ready' && 'animate-pulse')} />
          {banner.text}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {/* §9.375, FB-669 - the owner: *"that note add in mapovanie stolov, when you click on the
          * table and you have these položky, to be note also visible there."* Above the items,
          * because it is usually about one of them. Same reader as the open-table card. */}
        {noteOf(order) !== '' && (
          <p
            className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1.5 whitespace-pre-line rounded-xl border border-amber-500/30 bg-amber-500/10 px-2.5 py-2"
            data-table-order-note
          >
            <span className="shrink-0">📝</span>
            {noteOf(order)}
          </p>
        )}
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <T>Položky</T>
          </p>
          <button
            type="button"
            onClick={() => void markAllServed(order.id ?? 0, lines)}
            disabled={everythingServed || isMarkingAll}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCheck className="w-3.5 h-3.5" /> <T>Všetko servírované</T>
          </button>
        </div>

        {lines.map((line) => {
          const served = servedOf(line) === 'served'
          return (
            <button
              key={line.id}
              type="button"
              onClick={() => void setServed(order.id ?? 0, line, served ? 'pending' : 'served')}
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border text-left transition-colors',
                served
                  ? 'bg-emerald-500/10 border-emerald-500/25'
                  : 'bg-muted/40 border-border hover:bg-muted/60',
              )}
            >
              <span
                className={cn(
                  'w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0',
                  served ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-border',
                )}
              >
                {served && <Check className="w-3.5 h-3.5" />}
              </span>
              <span
                className={cn(
                  'flex-1 text-sm font-medium',
                  served && 'line-through text-muted-foreground',
                )}
              >
                <span className="text-primary font-bold">{line.quantity}×</span>{' '}
                <T>{line.productName ?? ''}</T>
              </span>
              <span className="text-sm font-bold">
                {formatCents(lineTotalCents(line.unitPrice, line.quantity ?? 0))}
              </span>
            </button>
          )
        })}

        {lines.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            <T>Žiadne položky</T>
          </p>
        )}
      </div>

      <div className="border-t border-border p-3 space-y-2">
        <div className="flex items-center justify-between text-sm font-bold px-1">
          <span>
            <T>Spolu</T>
          </span>
          <span className="text-primary text-base">{formatMoney(order.total)}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {/* §9.126 - the same destination as the open-tables card, so the same word.
            * §9.744 - and the same rights: drawn only for somebody holding `edit_order` and `new_order`. */}
          {canEdit && <PanelAction icon={Pencil} label={tt('Upraviť')} onClick={onGoToAdd} marker="edit" />}
          <PanelAction
            icon={Wallet}
            label={tt('Zaplatiť')}
            onClick={onGoToPay}
            className="bg-emerald-500 text-white hover:bg-emerald-400 border-transparent"
          />
          <PanelAction
            icon={Ban}
            label={tt('Stornovať')}
            onClick={onGoToCancel}
            className="bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20"
          />
          {/* §9.442, FB-761 - "in Mapovanie Stolov Next to Stornovať «Storno fiškál»". Hidden
            * unless a receipt exists and is unreversed; `PanelAction`'s own classes so the three
            * buttons stay one row. */}
          {order !== null && (
            <FiskalStornoAction
              order={order}
              onDone={onItemsChanged}
              compact
              label={tt('Storno fiškál')}
              className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border bg-red-500/10 text-red-500 border-red-500/20 text-xs font-bold hover:bg-red-500/20 transition-colors"
            />
          )}
        </div>
        {tableName && (
          <p className="text-[10px] text-muted-foreground text-center">
            <T>Stôl</T> {tableName}
          </p>
        )}
      </div>
    </PanelShell>
  )
}

function PanelAction({
  icon: Icon,
  label,
  onClick,
  className,
  marker,
}: {
  icon: LucideIcon
  label: string
  onClick: () => void
  className?: string
  /** §9.744 - `data-panel-action`, so a test can name the action rather than its translated label. */
  marker?: string
}) {
  return (
    <button
      type="button"
      data-panel-action={marker}
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border border-border text-xs font-bold hover:bg-muted transition-colors',
        className,
      )}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  )
}

const STATUS_DOTS: Record<TableStatus, string> = {
  free: 'bg-muted-foreground/40',
  in_kitchen: 'bg-amber-500',
  ready: 'bg-blue-500 animate-pulse',
  served: 'bg-emerald-500',
  paid: 'bg-zinc-500',
}

function PanelShell({
  title,
  subtitle,
  status,
  onClose,
  children,
}: {
  title: string
  subtitle?: string
  status: TableStatus
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <>
      {/* Transparent past the phone breakpoint: on a tablet the floor plan
          stays legible beside the panel, which is how a waiter uses both. */}
      <div className="fixed inset-0 z-30 bg-black/40 md:bg-transparent" onClick={onClose} />
      <div
        data-order-panel={status}
        className="fixed inset-y-0 right-0 z-40 w-full sm:max-w-md bg-card border-l border-border shadow-2xl flex flex-col"
      >
        <div className="flex items-start gap-3 px-4 py-3 border-b border-border">
          <span className={cn('w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0', STATUS_DOTS[status])} />
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold truncate">{title}</h2>
            {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
          </div>
          <button
            type="button"
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            className={BUTTON_GHOST}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </>
  )
}
